import {
  AgentChatThreadId,
  AgentChatThreadType,
  DataBrowserTab,
  DataConnectionId,
  DataSourceFilter,
  EditSessionBranchId,
  GridLayoutId,
  HexId,
  HexVersionId,
  RichTextDocument,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticViewName,
  TaoProjectId,
  assertNever,
  nullthrows,
} from "@hex/common";

import type { PickerSelection } from "../components/agent/shared/hooks/usePickerSelection.js";
import type { PromptEditIntent } from "../components/data-app/edit/detectPromptEditIntent.js";
import {
  DataBrowserRecommendedViewPanel,
  DataBrowserRecommendedViewSelection,
} from "../components/data/data-browser-recommended-view/utils/recommendedView.js";
import { RichTextMentionData } from "../components/rich-text/plugins/RichTextMentionPlugin.js";
import { CellFilterShowType } from "../components/sidebar/constants.js";
import {
  COLLAPSED_SIDEBAR_WIDTH,
  DEFAULT_AGENT_CHAT_SIDEBAR_WIDTH,
  DEFAULT_CHART_EDIT_PANEL_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
  SidebarTab,
} from "../components/sidebar/SidebarOptions.js";
import {
  DEFAULT_LINEAGE_FLOATING_CORNER,
  DEFAULT_LINEAGE_FLOATING_HEIGHT,
  DEFAULT_LINEAGE_FLOATING_WIDTH,
  type TaoLineageFloatingCorner,
} from "../components/tao-project/detail/lineage/taoLineageFloatingPanelOptions.js";
import type {
  TaoLineageLayout,
  TaoLineageOrientation,
} from "../components/tao-project/detail/lineage/taoLineageView.js";
import { DATA_BROWSER_EMPTY_SELECTION } from "../redux/slices/dataBrowserSlice.js";
import { DEFAULT_GRAPH_WIDTH } from "../views/constants.js";

import {
  BrowserStorageKey,
  useBrowserStorageGetter,
  useBrowserStorageSetter,
  useBrowserStorageValue,
  useDynamicBrowserStorageGetter,
  useDynamicBrowserStorageSetter,
} from "./useBrowserStorage.js";

export type SessionStorageKey<V> = {
  keyName: string;
  default: V;
};

export interface StoredScrollState {
  /** The hexId for which the scroll state was saved */
  hexId: HexId | null;
  /** The last vertical scroll position */
  scrollPosition: number | null;
  /** For layout view, the last active tab */
  layoutActiveTabId?: GridLayoutId | null;
}

export type SemanticDataBrowserPage =
  | {
      semanticProjectId?: SemanticProjectId;
      semanticDatasetName?: SemanticDatasetName;
    }
  | {
      semanticProjectId?: SemanticProjectId;
      semanticViewName?: SemanticViewName;
    };

/**
 * Get the session storage key for the agent chat thread.
 * @param hexId - The hexId of the project.
 * @param threadType - The type of thread to get the key for.
 * @returns The session storage key for the agent chat thread.
 */
export const getAgentChatThreadKey = (
  hexId: HexId,
  threadType: AgentChatThreadType,
): BrowserStorageKey<AgentChatThreadId | null> => {
  switch (threadType) {
    case AgentChatThreadType.ASK:
      return SessionStorageKeys.AGENT_ASK_CHAT_THREAD(nullthrows(hexId));
    case AgentChatThreadType.LOGIC_VIEW_V2:
      return SessionStorageKeys.AGENT_CHAT_THREAD_V2(nullthrows(hexId));
    case AgentChatThreadType.EXPLORE:
      return SessionStorageKeys.AGENT_EXPLORE_CHAT_THREAD(nullthrows(hexId));
    case AgentChatThreadType.SEMANTIC_AUTHORING:
      throw new Error(
        "Semantic authoring chat threads require a semanticProjectId",
      );
    case AgentChatThreadType.LOGIC_VIEW:
      throw new Error("Logic view v1 chat threads are no longer supported");
    case AgentChatThreadType.CHAT_WITH_APP:
      throw new Error("App agent chat threads should not use session storage");
    case AgentChatThreadType.REVIEW:
      throw new Error(
        "Review agent chat threads should not use session storage",
      );
    case AgentChatThreadType.ARTIFACT_EXTRACTION:
      throw new Error(
        "Artifact extraction threads should not use session storage",
      );
    case AgentChatThreadType.HERO_ANALYZER:
      throw new Error("Hero analyzer threads should not use session storage");
    case AgentChatThreadType.HERO_AGGREGATOR:
      throw new Error("Hero aggregator threads should not use session storage");
    case AgentChatThreadType.KNOWLEDGE_CURATION:
      throw new Error(
        "Knowledge curation threads should not use session storage",
      );
    case AgentChatThreadType.CODE_PROJECT:
      throw new Error(
        "Code project agent threads should not use session storage",
      );
    default:
      assertNever(threadType, threadType);
  }
};

/**
 * sessionStorage is similar to localStorage;
 * the difference is that while data in localStorage doesn't expire,
 * data in sessionStorage is cleared when the page session ends
 */
export const SessionStorageKeys = {
  SCROLL_STATE: (
    view: "logic" | "layout",
  ): SessionStorageKey<StoredScrollState> => ({
    keyName: `${view}_scroll_state`,
    default: {
      hexId: null,
      scrollPosition: null,
    },
  }),
  REVIEW_REQUEST_EDITOR: (
    reviewRequestLinkId: string,
  ): SessionStorageKey<null | RichTextDocument> => ({
    keyName: `review_request_editor_${reviewRequestLinkId}`,
    default: null,
  }),
  APP_SIDEBAR_VIEW: (
    hexId: HexId,
  ): BrowserStorageKey<"comments" | "schedules" | null> => ({
    keyName: `app_sidebar_${hexId}`,
    default: null,
  }),
  SCHEDULES_SIDEBAR_TAB: (
    hexId: HexId,
  ): BrowserStorageKey<"notifications" | "runs"> => ({
    keyName: `schedules_sidebar_${hexId}`,
    default: "notifications",
  }),
  CELL_FILTER_SHOW_TYPE: (
    hexId: HexId,
  ): SessionStorageKey<
    (typeof CellFilterShowType)[keyof typeof CellFilterShowType]
  > => ({
    keyName: `cell_filter_show_type_${hexId}`,
    default: CellFilterShowType.All,
  }),
  GRAPH_OPEN: (hexId: HexId) => ({
    keyName: `hex_${hexId}_graph_open`,
    default: false,
  }),
  GRAPH_WIDTH: (hexId: HexId) => ({
    keyName: `hex_${hexId}_graph_width`,
    default: DEFAULT_GRAPH_WIDTH,
  }),
  DIALOG_GRAPH_OPEN: (hexId: HexId) => ({
    keyName: `hex_${hexId}_dialog_graph_open`,
    default: false,
  }),
  DIALOG_GRAPH_WIDTH: (hexId: HexId) => ({
    keyName: `hex_${hexId}_dialog_graph_width`,
    default: DEFAULT_GRAPH_WIDTH,
  }),
  OUTLINE_COLLAPSED: (hexId: HexId) => ({
    keyName: `hex_${hexId}_outline_collapsed`,
    default: true,
  }),
  SIDEBAR_VIEW: (hexId: HexId) => ({
    keyName: `hex_${hexId}_sidebar_view`,
    default: SidebarTab.TOC,
  }),
  OUTLINE_V2_VIEW: (hexId: HexId) => ({
    keyName: `hex_${hexId}_sidebar_outline_v2_view`,
    default: SidebarTab.TOC,
  }),
  SIDEBAR_WIDTH: (hexId: HexId) => ({
    keyName: `hex_${hexId}_sidebar_width`,
    default: COLLAPSED_SIDEBAR_WIDTH,
  }),
  TAO_PROJECT_EXPANDED_CODE_CELLS: (
    projectId: TaoProjectId,
  ): BrowserStorageKey<readonly string[]> => ({
    keyName: `tao_project_${projectId}_expanded_code_cells`,
    default: [],
  }),
  TAO_BRANCH_METADATA_SIDEBAR_COLLAPSED: (branchId: EditSessionBranchId) => ({
    keyName: `plain_code_branch_${branchId}_metadata_sidebar_collapsed`,
    default: true,
  }),
  TAO_BRANCH_METADATA_SIDEBAR_WIDTH: (branchId: EditSessionBranchId) => ({
    keyName: `plain_code_branch_${branchId}_metadata_sidebar_width`,
    default: COLLAPSED_SIDEBAR_WIDTH,
  }),
  TAO_BRANCH_LINEAGE_PANEL_HEIGHT: (branchId: EditSessionBranchId) => ({
    keyName: `plain_code_branch_${branchId}_lineage_panel_height`,
    default: 360,
  }),
  TAO_LINEAGE_LAYOUT: {
    // v2 dropped the "left" layout; the new name skips tabs still holding it.
    keyName: `plain_code_lineage_layout_v2`,
    default: "bottom" as TaoLineageLayout,
  },
  TAO_LINEAGE_ORIENTATION: {
    keyName: `plain_code_lineage_graph_orientation`,
    default: "horizontal" as TaoLineageOrientation,
  },
  TAO_BRANCH_LINEAGE_FLOATING_WIDTH: (branchId: EditSessionBranchId) => ({
    keyName: `plain_code_branch_${branchId}_lineage_floating_width`,
    default: DEFAULT_LINEAGE_FLOATING_WIDTH,
  }),
  TAO_BRANCH_LINEAGE_FLOATING_HEIGHT: (branchId: EditSessionBranchId) => ({
    keyName: `plain_code_branch_${branchId}_lineage_floating_height`,
    default: DEFAULT_LINEAGE_FLOATING_HEIGHT,
  }),
  TAO_BRANCH_LINEAGE_FLOATING_CORNER: (
    branchId: EditSessionBranchId,
  ): BrowserStorageKey<TaoLineageFloatingCorner> => ({
    keyName: `plain_code_branch_${branchId}_lineage_floating_corner`,
    default: DEFAULT_LINEAGE_FLOATING_CORNER,
  }),
  SEMANTIC_WORKBENCH_SIDEBAR_WIDTH: (
    semanticProjectId: SemanticProjectId,
    sidebar: "main" | "bottom" | "right",
  ) => ({
    keyName: `semantic_project_${semanticProjectId}_${sidebar}_sidebar_width`,
    default:
      sidebar === "right"
        ? DEFAULT_AGENT_CHAT_SIDEBAR_WIDTH
        : DEFAULT_SIDEBAR_WIDTH,
  }),
  CHAT_WIDTH: (hexId: HexId) => ({
    keyName: `hex_${hexId}_chat_width`,
    default: DEFAULT_AGENT_CHAT_SIDEBAR_WIDTH,
  }),
  APP_AGENT_SIDEBAR_WIDTH: (hexId: HexId) => ({
    keyName: `hex_${hexId}_app_agent_width`,
    default: DEFAULT_AGENT_CHAT_SIDEBAR_WIDTH,
  }),
  CHART_EDIT_PANEL_WIDTH: (hexId: HexId) => ({
    keyName: `hex_${hexId}_chart_edit_panel_width`,
    default: DEFAULT_CHART_EDIT_PANEL_WIDTH,
  }),
  HIGHLIGHT_VARIABLE_SECTION: (
    hexId: HexId,
    section: "hex_user_attributes",
  ) => ({
    keyName: `hex_${hexId}_highlight_variable_${section}`,
    default: false,
  }),
  OUTLINE_DOCKED: (hexId: HexId) => ({
    keyName: `hex_${hexId}_outline_docked`,
    default: false,
  }),
  DATA_BROWSER_VIEW: (): BrowserStorageKey<DataBrowserTab> => ({
    keyName: "data_browser_view",
    default: DataBrowserTab.DATA,
  }),
  DATA_BROWSER_SEMANTIC_TAB_PAGE:
    (): BrowserStorageKey<SemanticDataBrowserPage> => ({
      keyName: "data_browser_semantic_tab_page",
      default: {},
    }),
  DATA_BROWSER_RECOMMENDED_View: (): BrowserStorageKey<{
    panel: DataBrowserRecommendedViewPanel;
    selection: DataBrowserRecommendedViewSelection;
  }> => ({
    keyName: "data_browser_recommended_secondary_panel",
    default: {
      panel: DataBrowserRecommendedViewPanel.OVERVIEW,
      selection: DATA_BROWSER_EMPTY_SELECTION,
    },
  }),
  AGENT_CHAT_THREAD_V2: (hexId: HexId) => ({
    keyName: `hex_${hexId}_agent_chat_thread_v2`,
    default: null,
  }),
  AGENT_ASK_CHAT_THREAD: (hexId: HexId) => ({
    keyName: `hex_${hexId}_agent_ask_chat_thread`,
    default: null,
  }),
  AGENT_EXPLORE_CHAT_THREAD: (hexId: HexId) => ({
    keyName: `hex_${hexId}_agent_explore_chat_thread`,
    default: null,
  }),
  AGENT_SEMANTIC_AUTHORING_CHAT_THREAD: (
    semanticProjectId: SemanticProjectId,
  ) => ({
    keyName: `semantic_${semanticProjectId}_agent_semantic_authoring_chat_thread`,
    default: null,
  }),
  ONBOARDING_DATA_CONNECTION_TYPE_TO_OPEN: () => ({
    keyName: "onboarding_data_connection_type_to_open",
    default: null,
  }),
  LOGIC_AGENT_CHAT_INITIAL_MENTIONED_CELL_IDS: (hexId: HexId) => ({
    keyName: `hex_${hexId}_logic_agent_initial_mentioned_cell_ids`,
    default: null,
  }),
  /**
   * Logic-view agent prompt draft per project + thread.
   * `NEW_THREAD` = no thread yet; drafts are per existing thread, with carry-over
   * from an existing thread → new thread only (see AgentPromptDraftProvider).
   */
  AGENT_PROMPT_DRAFT: (
    hexId: HexId,
    threadId: AgentChatThreadId | null,
  ): BrowserStorageKey<RichTextDocument | null> => ({
    keyName: `hex_${hexId}_${threadId ?? "NEW_THREAD"}_agent_prompt_draft`,
    default: null,
  }),
  /** Tao prompt bar draft, scoped to the thread or, with none selected, the location. */
  TAO_PROMPT_DRAFT: (
    scope: string,
  ): BrowserStorageKey<RichTextDocument | null> => ({
    keyName: `tao_prompt_draft_${scope}`,
    default: null,
  }),
  /** Model / effort picked for a Tao thread that does not exist yet; per location, cleared on first send. */
  TAO_PROMPT_PICKER_SELECTION: (
    scope: string,
  ): BrowserStorageKey<PickerSelection | null> => ({
    keyName: `tao_prompt_picker_selection_${scope}`,
    default: null,
  }),
  // Keep the legacy threads_* key so active sessions retain their selected agent data filter.
  AGENT_DATA_SOURCE_FILTER: (): BrowserStorageKey<DataSourceFilter | null> => ({
    keyName: "threads_agent_data_source_filter",
    default: null,
  }),
  THREADS_FROM_DATA_BROWSER: (): BrowserStorageKey<
    | ((
        | RichTextMentionData.Table
        | RichTextMentionData.SemanticDataset
        | RichTextMentionData.SemanticView
      ) & { connectionId: DataConnectionId })
    | null
  > => ({
    keyName: "threads_from_data_browser",
    default: null,
  }),
  THREAD_INSPECTOR_WIDTH: {
    keyName: "thread_inspector_width",
    default: 450,
  },
  EVAL_SUITE_DEFINITION_DRAWER_WIDTH: {
    keyName: "eval_suite_definition_drawer_width",
    default: 720,
  },
  /** Set before navigating to Slack integrations settings to trigger the threads-setup tour. */
  SHOW_SLACK_AGENT_TOUR: (): BrowserStorageKey<boolean> => ({
    keyName: "hex-slack-threads-setup-flow",
    default: false,
  }),
  APP_TYPE_CHOOSER_DISMISSED: (
    hexVersionId: HexVersionId,
  ): SessionStorageKey<boolean> => ({
    keyName: `app_type_chooser_dismissed_${hexVersionId}`,
    default: false,
  }),
  /**
   * Armed when the user has just committed to a generative-app build via a
   * path that fires a prompt (home "Generate an app" intent, or chooser
   * idle "Custom" + submit). Read by DataAppView so the "Building your
   * app…" purple loading state shows immediately, without waiting for the
   * parent agent to delegate to the data-app subagent. Cleared once a real
   * data app is delivered, otherwise tab-scoped (intentionally lost on
   * close — we don't want to mislead users in unrelated reload cases).
   */
  GEN_APP_LOADING_ARMED: (
    hexVersionId: HexVersionId,
  ): SessionStorageKey<boolean> => ({
    keyName: `gen_app_loading_armed_${hexVersionId}`,
    default: false,
  }),
  /** The dismiss on the Ask eval invitation: the callout stays away for the session. */
  EVAL_INVITATION_SNOOZED: {
    keyName: "eval_invitation_snoozed",
    default: false,
  },
  /** The X on the composer's Select nudge: that kind of card stays away for the session. */
  DATA_APP_SELECT_NUDGE_SNOOZED: (
    kind: PromptEditIntent,
  ): SessionStorageKey<boolean> => ({
    keyName: `data_app_select_nudge_snoozed_${kind}`,
    default: false,
  }),
  /**
   * Whether the user opened the fullscreen recent-threads sidebar. Selecting a
   * thread remounts the project thread view, so this has to outlive the
   * component to survive a thread switch. Never defaults open.
   * @deprecated Use {@link SessionStorageKeys.FULLSCREEN_SIDEBAR_PANEL_ID}
   * instead.
   */
  RECENT_THREADS_SIDEBAR_OPEN: (hexId: HexId): SessionStorageKey<boolean> => ({
    keyName: `hex_${hexId}_recent_threads_sidebar_open`,
    default: false,
  }),
  /**
   * Which fullscreen sidebar panel the user opened, if any.
   */
  FULLSCREEN_SIDEBAR_PANEL_ID: (
    hexId: HexId,
  ): SessionStorageKey<string | null> => ({
    keyName: `hex_${hexId}_fullscreen_sidebar_panel_id`,
    default: null,
  }),
};

export function useSessionStorageGetter<V>(key: BrowserStorageKey<V>): () => V {
  return useBrowserStorageGetter("Session", key);
}
export function useDynamicSessionStorageGetter(): <V>(
  key: BrowserStorageKey<V>,
) => V {
  return useDynamicBrowserStorageGetter("Session");
}
export function useSessionStorageSetter<V>(
  key: BrowserStorageKey<V>,
): (value: ((oldValue: V) => V) | V) => void {
  return useBrowserStorageSetter("Session", key);
}
export function useDynamicSessionStorageSetter(): <V>(
  key: BrowserStorageKey<V>,
  value: ((oldValue: V) => V) | V,
) => void {
  return useDynamicBrowserStorageSetter("Session");
}
export function useSessionStorageValue<V>(key: BrowserStorageKey<V>): V {
  return useBrowserStorageValue("Session", key);
}

export function useSessionStorage<V>(
  key: BrowserStorageKey<V>,
): [V, (value: V | ((oldValue: V) => V)) => void] {
  const value = useSessionStorageValue(key);
  const setter = useSessionStorageSetter(key);

  return [value, setter];
}
